import { useState, useEffect, useCallback, useRef } from "react";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { X, Loader2, Upload, Globe, CheckCircle2, Clock, AlertCircle, Copy, RefreshCw, Trash2, ExternalLink, ShieldCheck } from "lucide-react";

const TIMEZONES = [
  "(GMT-12:00) Baker Island", "(GMT-11:00) Midway", "(GMT-10:00) Honolulu", "(GMT-09:00) Anchorage",
  "(GMT-08:00) Los Angeles", "(GMT-07:00) Denver", "(GMT-06:00) Mexico City", "(GMT-05:00) New York",
  "(GMT-04:00) Santiago", "(GMT-03:00) Buenos Aires", "(GMT-03:00) Sao Paulo", "(GMT-02:00) South Georgia",
  "(GMT-01:00) Azores", "(GMT+00:00) London", "(GMT+00:00) Lisbon", "(GMT+01:00) Berlin",
  "(GMT+01:00) Brussels", "(GMT+01:00) Madrid", "(GMT+01:00) Paris", "(GMT+01:00) Rome",
  "(GMT+02:00) Athens", "(GMT+02:00) Bucharest", "(GMT+02:00) Cairo", "(GMT+02:00) Helsinki",
  "(GMT+03:00) Moscow", "(GMT+03:00) Istanbul", "(GMT+03:30) Tehran", "(GMT+04:00) Dubai",
  "(GMT+05:00) Karachi", "(GMT+05:30) Mumbai", "(GMT+06:00) Dhaka", "(GMT+07:00) Bangkok",
  "(GMT+08:00) Hong Kong", "(GMT+08:00) Singapore", "(GMT+09:00) Tokyo", "(GMT+09:00) Seoul",
  "(GMT+10:00) Sydney", "(GMT+11:00) Solomon Islands", "(GMT+12:00) Auckland"
];

const BannerUploadSlots = ({
  authAxios, settings, update, uploading, setUploading, field,
  uploadKeyPrefix, testIdPrefix, title, description, itemLabel, previewFit = "cover",
}) => (
  <div className="space-y-3">
    <Label className="font-bold">{title}</Label>
    <p className="text-xs text-muted-foreground">{description}</p>
    <div className="grid grid-cols-3 gap-3">
      {[0, 1, 2].map(idx => {
        const images = settings[field] || [];
        const imageUrl = images[idx] || "";
        const uploadKey = `${uploadKeyPrefix}_${idx}`;
        return (
          <div key={idx} className="space-y-1">
            <Label className="text-xs">{itemLabel} {idx + 1}</Label>
            <label
              className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer block h-32 flex flex-col items-center justify-center ${uploading[uploadKey] ? "border-emerald-400 bg-emerald-50" : imageUrl ? "border-emerald-400 bg-emerald-50/30 p-0 overflow-hidden" : "border-border hover:border-primary/50"}`}
              data-testid={`${testIdPrefix}-${idx}`}
            >
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 5 * 1024 * 1024) { toast.error("Imagem muito grande (max 5MB)"); return; }
                setUploading(prev => ({ ...prev, [uploadKey]: true }));
                try {
                  const formData = new FormData();
                  formData.append("file", file);
                  const res = await authAxios.post("/restaurant/upload-image", formData, { headers: { "Content-Type": "multipart/form-data" } });
                  const nextImages = [...(settings[field] || [])];
                  while (nextImages.length <= idx) nextImages.push("");
                  nextImages[idx] = res.data.url;
                  update(field, nextImages);
                  await authAxios.patch("/marketing/settings", { [field]: nextImages });
                  toast.success(`${itemLabel} ${idx + 1} salvo!`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Erro ao enviar imagem");
                }
                setUploading(prev => ({ ...prev, [uploadKey]: false }));
                e.target.value = "";
              }} />
              {uploading[uploadKey] ? (
                <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
              ) : imageUrl ? (
                <img src={imageUrl} alt={`${itemLabel} ${idx + 1}`} className={`w-full h-full rounded-lg ${previewFit === "contain" ? "object-contain" : "object-cover"}`} />
              ) : (
                <>
                  <Upload className="w-5 h-5 text-muted-foreground mb-1" />
                  <p className="text-[10px] text-muted-foreground">1300x400px</p>
                </>
              )}
            </label>
            {imageUrl && (
              <button className="text-[10px] text-red-400 hover:text-red-600" onClick={() => {
                const nextImages = [...(settings[field] || [])];
                nextImages[idx] = "";
                const cleaned = nextImages.filter(Boolean);
                update(field, cleaned);
                authAxios.patch("/marketing/settings", { [field]: cleaned });
              }}>Remover</button>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

export const PersonalizacionTab = ({ authAxios, restaurant }) => {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDomainDialog, setShowDomainDialog] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [uploading, setUploading] = useState({});
  const [domainConfig, setDomainConfig] = useState(null);
  const [domainLoading, setDomainLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchDomainStatus = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/restaurant/domain/status");
      setDomainConfig(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchDomainStatus(); }, [fetchDomainStatus]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    authAxiosRef.current.get("/marketing/settings").then(r => { setSettings(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));
  const updateColor = (key, value) => setSettings(prev => ({ ...prev, menu_colors: { ...prev.menu_colors, [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await authAxios.patch("/marketing/settings", {
        menu_colors: settings.menu_colors,
        menu_font: settings.menu_font,
        logo_url: settings.logo_url || "",
        logo_format: settings.logo_format || "round",
        banner_video_url: settings.banner_video_url || "",
        banner_url: settings.banner_url,
        banner_text: settings.banner_text,
        banners: settings.banners || [],
        menu_search_banners: settings.menu_search_banners || [],
        catalog_style: settings.catalog_style || "single_page",
        show_prices: settings.show_prices !== false,
        show_images: settings.show_images !== false,
        dark_mode: settings.dark_mode !== false,
        custom_css: settings.custom_css || "",
        logo_format: settings.logo_format || "round",
        template: settings.template || "single_page",
        timezone: settings.timezone || "Europe/Madrid",
        auto_confirm_orders: settings.auto_confirm_orders !== false,
        enable_observations: settings.enable_observations !== false,
        disable_customer_auth: settings.disable_customer_auth || false,
        table_menu_readonly: settings.table_menu_readonly || false,
        table_payment_enabled: settings.table_payment_enabled || false,
        auto_dispatch: settings.auto_dispatch || false,
        adults_only: settings.adults_only || false,
        allow_whatsapp_order: settings.allow_whatsapp_order !== false,
        validate_marketplace_stock: settings.validate_marketplace_stock || false,
        cancel_orders_after_minutes: settings.cancel_orders_after_minutes || 60,
        finalize_orders_after_minutes: settings.finalize_orders_after_minutes || "",
        custom_domain: settings.custom_domain || "",
        title_font_size: settings.title_font_size || "2.5",
        address_prompt: settings.address_prompt || "add_item",
        variation_counting: settings.variation_counting || "by_product"
      });
      toast.success("Personalizacion guardada");
    } catch { toast.error("Error al guardar"); }
    setSaving(false);
  };

  if (loading || !settings) return <LoadingScreen />;

  const colors = settings.menu_colors || { primary: "#dc2626", secondary: "#eab308", bg: "#121212", text: "#fafafa" };
  const subdomain = restaurant?.name?.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") || "mi-tienda";

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="uppercase">Dominio compartido</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Subdominio</Label>
            <div className="flex items-center gap-2">
              <Input value={subdomain} className="bg-secondary border-transparent flex-1" readOnly />
              <span className="text-sm text-muted-foreground">.kaisosystem.com</span>
            </div>
          </div>
          <Separator />
          <div className="space-y-3">
            <Label className="font-bold">Dominio propio</Label>
            {domainConfig?.domain && domainConfig.status !== "none" ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  <span className="font-mono text-sm font-semibold">{domainConfig.domain}</span>
                  {domainConfig.status === "active" ? (
                    <Badge className="bg-emerald-100 text-emerald-700 gap-1" data-testid="domain-status-active"><CheckCircle2 className="w-3 h-3" /> Ativo</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 gap-1" data-testid="domain-status-pending"><Clock className="w-3 h-3" /> Pendente</Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setShowDomainDialog(true)} data-testid="domain-manage-btn">
                    <ExternalLink className="w-3 h-3" /> Ver configuracao DNS
                  </Button>
                  <Button variant="outline" size="sm" className="gap-1.5 text-xs text-red-500 border-red-200 hover:bg-red-50" onClick={async () => {
                    try {
                      await authAxios.delete("/restaurant/domain");
                      setDomainConfig({ domain: null, status: "none" });
                      update("custom_domain", "");
                      toast.success("Dominio removido");
                    } catch { toast.error("Erro ao remover dominio"); }
                  }} data-testid="domain-remove-btn">
                    <Trash2 className="w-3 h-3" /> Remover
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">Sin dominio propio configurado</p>
                <Button variant="link" className="text-emerald-600 p-0 h-auto font-bold uppercase" onClick={() => setShowDomainDialog(true)} data-testid="configure-domain-btn">
                  CONFIGURAR DOMINIO PROPIO
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="uppercase">Logo y Marca</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { label: "Logotipo", desc: "Cuadrado, mayor que 400x400px, menor que 5 MB", testId: "logo-upload-area", field: "logo_url", accept: "image/*" },
              { label: "Imagem de capa", desc: "1300x400px grande, 420x170px pequeno", testId: "cover-upload-area", field: "banner_url", accept: "image/*" },
              { label: "Fonte", desc: "Arquivo .ttf", testId: "font-upload-area", field: "custom_font_url", accept: ".ttf,.woff,.woff2" },
            ].map(item => (
              <div key={item.testId} className="space-y-2">
                <Label>{item.label}</Label>
                <label
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer block ${uploading[item.field] ? "border-emerald-400 bg-emerald-50" : settings[item.field] ? "border-emerald-400 bg-emerald-50/30" : "border-border hover:border-primary/50"}`}
                  data-testid={item.testId}
                >
                  <input
                    type="file"
                    accept={item.accept}
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploading(prev => ({ ...prev, [item.field]: true }));
                      try {
                        const formData = new FormData();
                        formData.append("file", file);
                        let newUrl;
                        if (item.field === "logo_url") {
                          const res = await authAxios.post("/restaurant/upload-logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
                          newUrl = res.data.logo_url;
                        } else {
                          const res = await authAxios.post("/restaurant/upload-image", formData, { headers: { "Content-Type": "multipart/form-data" } });
                          newUrl = res.data.url;
                        }
                        update(item.field, newUrl);
                        // Auto-save to marketing settings so public menu reflects immediately
                        await authAxios.patch("/marketing/settings", { [item.field]: newUrl });
                        toast.success(`${item.label} enviado e salvo!`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Erro ao enviar arquivo");
                      }
                      setUploading(prev => ({ ...prev, [item.field]: false }));
                      e.target.value = "";
                    }}
                  />
                  {uploading[item.field] ? (
                    <Loader2 className="w-6 h-6 mx-auto text-emerald-600 animate-spin mb-2" />
                  ) : settings[item.field] ? (
                    item.accept.startsWith("image") ? (
                      <img src={settings[item.field]} alt={item.label} className={`w-16 h-16 mx-auto object-cover mb-2 ${item.field === "logo_url" && (settings.logo_format || "round") === "round" ? "rounded-full" : "rounded-lg"}`} />
                    ) : (
                      <Upload className="w-6 h-6 mx-auto text-emerald-600 mb-2" />
                    )
                  ) : (
                    <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                  )}
                  <p className="text-xs text-muted-foreground">{settings[item.field] ? "Clique para trocar" : item.desc}</p>
                </label>
                {settings[item.field] && (
                  <button className="text-xs text-red-400 hover:text-red-600" onClick={() => update(item.field, "")}>Remover</button>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-xs text-muted-foreground">Formato do logo:</Label>
            <button onClick={() => update("logo_format", "round")} className={`px-3 py-1 text-xs rounded-full border ${(settings.logo_format || "round") === "round" ? "bg-primary text-white border-primary" : "border-border text-muted-foreground"}`} data-testid="logo-format-round">Redondo</button>
            <button onClick={() => update("logo_format", "square")} className={`px-3 py-1 text-xs rounded-lg border ${settings.logo_format === "square" ? "bg-primary text-white border-primary" : "border-border text-muted-foreground"}`} data-testid="logo-format-square">Quadrado</button>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
            Fontes no formato TTF podem ser baixadas na pagina <a href="https://fonts.google.com" target="_blank" rel="noreferrer" className="text-blue-800 underline font-medium">Google Fonts</a>.
          </div>

          <Separator />

          <BannerUploadSlots
            authAxios={authAxios}
            settings={settings}
            update={update}
            uploading={uploading}
            setUploading={setUploading}
            field="banners"
            uploadKeyPrefix="banner"
            testIdPrefix="banner-upload"
            title="Banners do Menu (3 imagens)"
            description="Suba ate 3 imagens de banner. Elas aparecem no carrossel do menu publico. Se subir um video, ele substitui os banners."
            itemLabel="Banner"
          />

          <Separator />

          <BannerUploadSlots
            authAxios={authAxios}
            settings={settings}
            update={update}
            uploading={uploading}
            setUploading={setUploading}
            field="menu_search_banners"
            uploadKeyPrefix="menu_search_banner"
            testIdPrefix="menu-search-banner-upload"
            title="Slides promocionais abaixo da busca (3 imagens)"
            description="Suba ate 3 imagens. Elas aparecem em um carrossel compacto abaixo da busca, independente da capa superior."
            itemLabel="Slide"
            previewFit="contain"
          />

          <Separator />

          {/* Video do Menu */}
          <div className="space-y-3">
            <Label className="font-bold">Video do Menu (opcional)</Label>
            <p className="text-xs text-muted-foreground">Se subir um video, ele reproduz em loop no topo do menu. Aceita MP4, WebM e MOV (max 50MB).</p>
            <label
              className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer block ${uploading.video ? "border-emerald-400 bg-emerald-50" : settings.banner_video_url ? "border-emerald-400 bg-emerald-50/30" : "border-border hover:border-primary/50"}`}
              data-testid="video-upload-area"
            >
              <input type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 50 * 1024 * 1024) { toast.error("Video muito grande (max 50MB)"); return; }
                setUploading(prev => ({ ...prev, video: true }));
                try {
                  const formData = new FormData();
                  formData.append("file", file);
                  const res = await authAxios.post("/restaurant/upload-image", formData, { headers: { "Content-Type": "multipart/form-data" } });
                  update("banner_video_url", res.data.url);
                  await authAxios.patch("/marketing/settings", { banner_video_url: res.data.url });
                  toast.success("Video enviado e salvo!");
                } catch (err) { toast.error(err.response?.data?.detail || "Erro ao enviar video"); }
                setUploading(prev => ({ ...prev, video: false }));
                e.target.value = "";
              }} />
              {uploading.video ? (
                <Loader2 className="w-6 h-6 mx-auto text-emerald-600 animate-spin mb-1" />
              ) : (
                <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-1" />
              )}
              <p className="text-xs text-muted-foreground">{settings.banner_video_url ? "Video enviado - clique para trocar" : "Clique para upload de video (MP4/WebM/MOV)"}</p>
            </label>
            {settings.banner_video_url && (
              <button className="text-xs text-red-400 hover:text-red-600" onClick={() => { update("banner_video_url", ""); authAxios.patch("/marketing/settings", { banner_video_url: "" }); }}>Remover video</button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="uppercase">Opciones de Diseno</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Formato del logo</Label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="logo_format" checked={(settings.logo_format || "round") === "square"} onChange={() => update("logo_format", "square")} />
                  <span>Cuadrado</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="logo_format" checked={(settings.logo_format || "round") === "round"} onChange={() => update("logo_format", "round")} />
                  <span>Redondo</span>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Template</Label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="template" checked={(settings.template || "single_page") === "single_page"} onChange={() => update("template", "single_page")} />
                  <span>Pagina unica</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="template" checked={(settings.template || "single_page") === "by_category"} onChange={() => update("template", "by_category")} />
                  <span>Paginas por categoria</span>
                </label>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Preguntar por la direccion</Label>
              <Select value={settings.address_prompt || "add_item"} onValueChange={v => update("address_prompt", v)}>
                <SelectTrigger className="bg-secondary border-transparent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="add_item">add_item</SelectItem>
                  <SelectItem value="checkout">checkout</SelectItem>
                  <SelectItem value="never">never</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tamano de la fuente del titulo</Label>
              <div className="flex items-center gap-2">
                <Input type="number" step="0.1" value={settings.title_font_size || "2.5"} onChange={e => update("title_font_size", e.target.value)} className="bg-secondary border-transparent w-24" />
                <span className="text-muted-foreground">rem</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="uppercase">Colores del Menu Publico</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: "primary", label: "Botones y links", def: "#dc2626" },
              { key: "secondary", label: "Texto secundario", def: "#1f2937" },
              { key: "accent", label: "Detalles y destaque", def: "#CDA34F" },
              { key: "bg", label: "Fondo del menu", def: "#ffffff" },
              { key: "text", label: "Texto principal", def: "#111827" },
              { key: "header_bg", label: "Fondo del cabecalho", def: "#ffffff" },
              { key: "category", label: "Categorias (pills)", def: "" },
              { key: "price", label: "Precios", def: "" },
            ].map(c => (
              <div key={c.key} className="space-y-1.5">
                <Label className="text-xs">{c.label}</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={colors[c.key] || c.def || colors.primary || "#dc2626"} onChange={e => updateColor(c.key, e.target.value)} className="w-10 h-10 rounded cursor-pointer border border-border" data-testid={`color-${c.key}`} />
                  <Input value={colors[c.key] || c.def || ""} onChange={e => updateColor(c.key, e.target.value)} placeholder="auto" className="bg-secondary border-transparent text-xs font-mono h-8" />
                </div>
              </div>
            ))}
          </div>
          {/* Preview */}
          <div className="border rounded-xl overflow-hidden" data-testid="color-preview">
            <div className="px-4 py-3 flex items-center gap-3" style={{ backgroundColor: colors.header_bg || colors.bg || "#fff" }}>
              <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: colors.primary || "#dc2626" }} />
              <span className="font-bold text-sm" style={{ color: colors.text || "#111" }}>{restaurant?.name || "Seu Restaurante"}</span>
              <div className="ml-auto px-3 py-1 rounded-full text-xs text-white font-bold" style={{ backgroundColor: colors.primary || "#dc2626" }}>
                Ver carrinho
              </div>
            </div>
            <div className="px-4 py-3" style={{ backgroundColor: colors.bg || "#fff" }}>
              <p className="text-xs font-bold mb-1" style={{ color: colors.text || "#111" }}>Exemplo de produto</p>
              <p className="text-sm font-bold" style={{ color: colors.primary || "#dc2626" }}>12.50 EUR</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="uppercase">Zona horaria</CardTitle></CardHeader>
        <CardContent>
          <Select value={settings.timezone || "(GMT+01:00) Madrid"} onValueChange={v => update("timezone", v)}>
            <SelectTrigger className="bg-secondary border-transparent" data-testid="timezone-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="uppercase">Opciones de la Tienda</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
            {[
              { key: "auto_confirm_orders", label: "Confirmar pedidos automaticamente", desc: null, default: true },
              { key: "auto_dispatch", label: "Enviar pedidos despachados automaticamente", desc: null, default: false },
              { key: "enable_observations", label: "Habilitar observaciones en los productos", desc: "Permitir dejar una observacion al comprar un producto", default: true },
              { key: "adults_only", label: "Solamente para adultos", desc: "Preguntar y dejar la tienda disponible solamente para mayores de edad", default: false },
              { key: "disable_customer_auth", label: "Desabilitar autenticacao de clientes", desc: "Habilite para que los clientes puedan realizar pedidos sin registrarse", default: true },
              { key: "allow_whatsapp_order", label: "Permitir enviar el pedido por WhatsApp", desc: "Habilita que el cliente pueda enviar el pedido por mensaje", default: true },
              { key: "table_menu_readonly", label: "Dejar el menu de mesa como solamente lectura", desc: "Os clientes podem visualizar, mas nao podem realizar pedidos", default: false },
              { key: "table_payment_enabled", label: "Ativar pagamentos na mesa", desc: "Permitir que clientes paguem online ao fazer pedidos na mesa. Se desativado, acumula e paga no final.", default: false },
              { key: "validate_marketplace_stock", label: "Validar stock en pedidos marketplace", desc: "Rechazar el pedido automaticamente si no huver stock disponible", default: false }
            ].map(opt => (
              <div key={opt.key} className={`flex items-start gap-3 py-2 ${opt.key === "table_payment_enabled" ? "bg-emerald-50 border border-emerald-200 rounded-lg p-3 -mx-1" : ""}`}>
                <Switch checked={settings[opt.key] !== undefined ? settings[opt.key] : opt.default} onCheckedChange={v => update(opt.key, v)} className="mt-0.5" data-testid={`toggle-${opt.key}`} />
                <div>
                  <p className="font-medium text-sm">{opt.label}</p>
                  {opt.desc && <p className="text-xs text-muted-foreground">{opt.desc}</p>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="uppercase">Gestion de Pedidos</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Agrupar clientes</Label>
              <Select value={settings.group_customers || "user_phone"} onValueChange={v => update("group_customers", v)}>
                <SelectTrigger className="bg-secondary border-transparent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user_phone">Por usuario y telefono</SelectItem>
                  <SelectItem value="user_only">Solo por usuario</SelectItem>
                  <SelectItem value="phone_only">Solo por telefono</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Finalizar pedidos despues de</Label>
              <div className="flex items-center gap-2">
                <Input type="number" value={settings.finalize_orders_after_minutes || ""} onChange={e => update("finalize_orders_after_minutes", e.target.value)} placeholder="" className="bg-secondary border-transparent w-20" />
                <span className="text-muted-foreground text-sm">minutos</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Cancelar pedidos no recibidos despues de *</Label>
              <div className="flex items-center gap-2">
                <Input type="number" value={settings.cancel_orders_after_minutes || 60} onChange={e => update("cancel_orders_after_minutes", Number(e.target.value))} className="bg-secondary border-transparent w-20" />
                <span className="text-muted-foreground text-sm">minutos</span>
              </div>
            </div>
          </div>
          <Separator />
          <div className="space-y-2">
            <Label>Contabilizar itens de variacion</Label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="variation_counting" checked={(settings.variation_counting || "by_product") === "by_product"} onChange={() => update("variation_counting", "by_product")} />
                <span>Por producto</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="variation_counting" checked={(settings.variation_counting || "by_product") === "by_item"} onChange={() => update("variation_counting", "by_item")} />
                <span>Por item</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Button className="glow-orange" onClick={save} disabled={saving} data-testid="save-personalizacion-btn">
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : "ACTUALIZAR TIENDA"}
      </Button>

      {showDomainDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDomainDialog(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="domain-dialog">
            <div className="sticky top-0 bg-white px-6 py-4 border-b flex items-center justify-between z-10">
              <h2 className="text-lg font-bold flex items-center gap-2"><Globe className="w-5 h-5 text-emerald-600" /> Configurar dominio propio</h2>
              <button onClick={() => setShowDomainDialog(false)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>

            <div className="p-6 space-y-6">
              {/* Step 1: Enter domain (only if no domain configured) */}
              {(!domainConfig?.domain || domainConfig.status === "none") ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800"><strong>Passo 1:</strong> Insira o dominio que voce comprou. Depois mostraremos as configuracoes DNS necessarias.</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-bold">Dominio *</Label>
                    <Input value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="meurestaurante.com" className="bg-gray-50 h-12 text-base" data-testid="domain-input" />
                    <p className="text-xs text-gray-400">Exemplo: kaisosushi.es, meubar.com.br</p>
                  </div>
                  <div className="flex gap-3">
                    <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" disabled={!newDomain || domainLoading} data-testid="save-domain-btn" onClick={async () => {
                      setDomainLoading(true);
                      try {
                        const res = await authAxios.post("/restaurant/domain/configure", { domain: newDomain });
                        setDomainConfig(res.data);
                        update("custom_domain", newDomain);
                        toast.success("Dominio configurado! Agora configure o DNS.");
                      } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
                      setDomainLoading(false);
                    }}>
                      {domainLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                      CONFIGURAR DOMINIO
                    </Button>
                    <Button variant="outline" onClick={() => setShowDomainDialog(false)}>Cancelar</Button>
                  </div>
                </div>
              ) : (
                /* Step 2: Show DNS configuration */
                <div className="space-y-5">
                  {/* Domain Header */}
                  <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
                    <Globe className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono font-bold text-sm truncate">{domainConfig.domain}</p>
                      <p className="text-xs text-gray-400">Configurado em {domainConfig.configured_at ? new Date(domainConfig.configured_at).toLocaleDateString() : "-"}</p>
                    </div>
                    {domainConfig.status === "active" ? (
                      <Badge className="bg-emerald-100 text-emerald-700 gap-1 flex-shrink-0"><CheckCircle2 className="w-3 h-3" /> Ativo</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 gap-1 flex-shrink-0"><Clock className="w-3 h-3" /> Pendente</Badge>
                    )}
                  </div>

                  {domainConfig.status !== "active" && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-bold mb-1">Configure os registros DNS abaixo</p>
                        <p>Acesse o painel do seu provedor de dominio (GoDaddy, Namecheap, Hostinger, etc.) e adicione os registros listados. A propagacao pode levar ate 48 horas.</p>
                      </div>
                    </div>
                  )}

                  {domainConfig.status === "active" && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-emerald-800">
                        <p className="font-bold mb-1">Dominio verificado e ativo!</p>
                        <p>Seu menu ja esta acessivel em <strong>https://{domainConfig.domain}</strong>. O QR Code e o link foram atualizados automaticamente.</p>
                      </div>
                    </div>
                  )}

                  {/* DNS Records Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-800 text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider">
                      Registros DNS necessarios
                    </div>
                    <div className="divide-y">
                      {(domainConfig.dns_records || []).map((record, idx) => (
                        <div key={idx} className="p-4 space-y-2 hover:bg-gray-50 transition-colors" data-testid={`dns-record-${idx}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-[10px] font-mono font-bold">{record.type}</Badge>
                            <span className="text-xs text-gray-500">{record.description}</span>
                          </div>
                          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                            <span className="text-gray-400 text-xs font-medium">Host:</span>
                            <div className="flex items-center gap-1.5">
                              <code className="bg-gray-100 px-2 py-0.5 rounded text-xs font-mono font-semibold break-all">{record.host}</code>
                              <button className="text-gray-400 hover:text-gray-600 flex-shrink-0" onClick={() => { navigator.clipboard.writeText(record.host); toast.success("Copiado!"); }}>
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                            <span className="text-gray-400 text-xs font-medium">Valor:</span>
                            <div className="flex items-center gap-1.5">
                              <code className="bg-gray-100 px-2 py-0.5 rounded text-xs font-mono font-semibold break-all">{record.value}</code>
                              <button className="text-gray-400 hover:text-gray-600 flex-shrink-0" onClick={() => { navigator.clipboard.writeText(record.value); toast.success("Copiado!"); }}>
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Verify Button */}
                  {domainConfig.status !== "active" && (
                    <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-sm font-bold gap-2" disabled={verifying} data-testid="verify-domain-btn" onClick={async () => {
                      setVerifying(true);
                      try {
                        const res = await authAxios.post("/restaurant/domain/verify");
                        setDomainConfig(prev => ({ ...prev, status: res.data.status, verified_at: res.data.verified ? new Date().toISOString() : null }));
                        if (res.data.verified) {
                          toast.success("Dominio verificado e ativo!");
                        } else {
                          toast.info(res.data.message);
                        }
                      } catch { toast.error("Erro ao verificar"); }
                      setVerifying(false);
                    }}>
                      {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      VERIFICAR CONFIGURACAO DNS
                    </Button>
                  )}

                  {/* Remove domain */}
                  <div className="pt-2 border-t">
                    <Button variant="ghost" size="sm" className="text-xs text-red-400 hover:text-red-600 gap-1.5" onClick={async () => {
                      try {
                        await authAxios.delete("/restaurant/domain");
                        setDomainConfig({ domain: null, status: "none" });
                        setNewDomain("");
                        update("custom_domain", "");
                        toast.success("Dominio removido");
                      } catch { toast.error("Erro"); }
                    }} data-testid="domain-dialog-remove">
                      <Trash2 className="w-3 h-3" /> Nao utilizar dominio propio
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
